// lib/demoCompanyService.ts
import { aiDemoGenerator } from './aiDemoGenerator';

export interface DemoCompany {
  id: string;
  name: string;
  registrationNumber: string;
  industry: string;
  location: string;
  contactName: string;
  contactTitle: string;
  email: string;
  phone: string;
  website: string;
  memberSince: string;
  companySize: string;
  yearEstablished: string;
  description: string;
  logo?: string;
  tradingName?: string;
  dateOfIncorporation?: string;
  registeredAddress?: string;
  postalAddress?: string;
  county?: string;
  linkedin?: string;
  industrySectors?: string[];
  numberOfSites?: string;
  geographicSpread?: string;
  certifications?: string[];
  facilityAge?: string;
  facilitySize?: string;
  operatingHours?: string;
  operatingDays?: string;
  monitoringSystems?: string[];
  connectivity?: string;
  preferredCommunication?: string;
  commercialModel?: string;
  budgetRange?: string;
}

export interface DemoSubmission {
  id: string;
  companyId: string;
  companyName: string;
  companyRegistration?: string;
  problemTitle: string;
  description: string;
  plainDescription?: string;
  estimatedImpact: string;
  estimatedImpactValue: number;
  urgency: 'high' | 'medium' | 'low';
  status: 'pending' | 'under_review' | 'accepted' | 'rejected';
  submittedAt: string;
  reviewedAt?: string;
  reviewNotes?: string;
  documents: { name: string; url: string; size?: string; type?: string }[];
  category?: string[];
  
  // Section 2: Contact Person
  contactName?: string;
  contactTitle?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactDepartment?: string;
  yearsInRole?: string;
  decisionMaker?: string;
  decisionMakerDetails?: string;
  secondaryName?: string;
  secondaryTitle?: string;
  secondaryEmail?: string;
  secondaryPhone?: string;

  // Section 3: Problem Description
  problemCategory?: string;
  otherCategory?: string;
  rootCause?: string;
  firstAppeared?: string;
  frequency?: string;
  seasonalMonths?: string;
  scope?: string;
  previousAttempts?: string;

  // Section 4: Financial Impact
  costEnergy?: number;
  costMaterial?: number;
  costDowntime?: number;
  costLabour?: number;
  costCompliance?: number;
  costOther?: number;
  costOtherDescription?: string;
  totalAnnualCost?: number;
  evidence?: string[];
  otherEvidence?: string;
  confidenceLevel?: string;
  estimateDerivation?: string;

  // Section 5: Market Prevalence
  industryPrevalence?: string;
  estimatedKenya?: number;
  estimatedEastAfrica?: number;
  affectedIndustries?: string;
  scalabilityFactor?: string;
  existingSolutions?: string;
  soughtHelp?: string[];

  // Section 6: Pilot Readiness
  pilotLikelihood?: string;
  approvalAuthority?: string;
  approvalTimeline?: string;
  pilotDuration?: string;
  successCriteria?: string;
  budgetRange?: string;
  commercialModel?: string;
  pilotAccess?: string[];

  // Section 7: Technical Environment
  facilityAge?: string;
  facilitySize?: string;
  operatingHours?: string;
  operatingDays?: string;
  monitoringSystems?: string[];
  connectivity?: string;
  primaryEquipment?: string;
  equipmentBrand?: string;
  equipmentAge?: string;
  maintenanceSpend?: number;
  lastOverhaul?: string;
  solutionType?: string[];
  technicalConstraints?: string;

  // Section 8: Regulatory Context
  regulatoryRelated?: string;
  regulatoryBodies?: string;
  certifications?: string[];
  upcomingDeadlines?: string;

  // Section 9: Self-Assessment
  severityRating?: string;
  urgencyRating?: string;
  prevalenceRating?: string;
  dataAvailabilityRating?: string;
  techOpennessRating?: string;
  managementSupportRating?: string;
  decisionSpeedRating?: string;
  additionalContext?: string;

  // Section 10: Consent
  consentStorage?: boolean;
  consentContact?: boolean;
  consentAnonymised?: boolean;
  consentUpdates?: boolean;
  consentSquad?: boolean;
  dataSharingLevel?: string;
  declarationAgreed?: boolean;
}

// Demo company accounts - UPDATED IDs to match Login.tsx
export const DEMO_COMPANIES: DemoCompany[] = [
  {
    id: 'company_kenya_breweries',
    name: 'Kenya Breweries Limited',
    registrationNumber: 'KBL/1990/4567',
    industry: 'fmcg',
    location: 'Nairobi, Kenya',
    contactName: 'John Mwangi',
    contactTitle: 'Plant Manager',
    email: 'demo@kenyabreweries.com',
    phone: '+254 722 123 456',
    website: 'https://kenyabreweries.com',
    memberSince: '2025-06-15',
    companySize: '501+',
    yearEstablished: '1990',
    description: 'Leading beverage manufacturer in East Africa, producing beers, ciders, and non-alcoholic beverages.',
    tradingName: 'Kenya Breweries',
    dateOfIncorporation: '1990-06-15',
    registeredAddress: 'Enterprise Road, Industrial Area, Nairobi',
    county: 'Nairobi',
    industrySectors: ['Food and Beverage Manufacturing', 'Agro-processing'],
    numberOfSites: '5',
    geographicSpread: 'Nairobi, Mombasa, Kisumu, Nakuru, Eldoret',
    certifications: ['ISO 9001', 'ISO 14001', 'ISO 22000'],
    facilityAge: '35',
    facilitySize: '150000',
    operatingHours: '24/7',
    operatingDays: '350',
    monitoringSystems: ['SCADA', 'ERP', 'IoT sensors'],
    connectivity: 'Reliable fibre broadband'
  },
  {
    id: 'company_bidco',
    name: 'Bidco Africa',
    registrationNumber: 'BID/1985/002',
    industry: 'fmcg',
    location: 'Thika, Kenya',
    contactName: 'Sarah Wanjiku',
    contactTitle: 'Operations Director',
    email: 'demo@bidcoafrica.com',
    phone: '+254 722 234 567',
    website: 'https://bidcoafrica.com',
    memberSince: '2025-08-20',
    companySize: '501+',
    yearEstablished: '1985',
    description: 'Leading manufacturer of edible oils, fats, and detergents in East Africa.',
    tradingName: 'Bidco',
    dateOfIncorporation: '1985-03-10',
    registeredAddress: 'Thika Industrial Area',
    county: 'Kiambu',
    industrySectors: ['Manufacturing', 'Agro-processing'],
    numberOfSites: '4',
    geographicSpread: 'Thika, Nairobi, Mombasa, Kisumu',
    certifications: ['ISO 9001', 'ISO 14001', 'ISO 22000'],
    facilityAge: '40',
    facilitySize: '200000',
    operatingHours: '24/7',
    operatingDays: '360',
    monitoringSystems: ['SCADA', 'ERP'],
    connectivity: 'Reliable fibre broadband'
  },
  {
    id: 'company_safaricom',
    name: 'Safaricom PLC',
    registrationNumber: 'SAF/1997/003',
    industry: 'telecommunications',
    location: 'Nairobi, Kenya',
    contactName: 'Peter Ndegwa',
    contactTitle: 'CEO',
    email: 'demo@safaricom.com',
    phone: '+254 722 345 678',
    website: 'https://safaricom.com',
    memberSince: '2025-09-10',
    companySize: '501+',
    yearEstablished: '1997',
    description: 'Leading mobile network operator in Kenya.',
    tradingName: 'Safaricom',
    dateOfIncorporation: '1997-08-01',
    registeredAddress: 'Safaricom House, Waiyaki Way, Nairobi',
    county: 'Nairobi',
    industrySectors: ['Telecommunications', 'Technology'],
    numberOfSites: '15',
    geographicSpread: 'Nationwide',
    certifications: ['ISO 9001', 'ISO 27001', 'ISO 20000'],
    facilityAge: '25',
    facilitySize: '50000',
    operatingHours: '24/7',
    operatingDays: '365',
    monitoringSystems: ['DCIM', 'IoT sensors', 'SCADA'],
    connectivity: 'Reliable fibre broadband'
  },
  {
    id: 'company_eabl',
    name: 'East African Breweries Limited',
    registrationNumber: 'EAB/1922/004',
    industry: 'fmcg',
    location: 'Nairobi, Kenya',
    contactName: 'Jane Akinyi',
    contactTitle: 'Supply Chain Manager',
    email: 'demo@eabl.com',
    phone: '+254 722 456 789',
    website: 'https://eabl.com',
    memberSince: '2025-10-05',
    companySize: '501+',
    yearEstablished: '1922',
    description: 'Leading beverage manufacturer in East Africa.',
    tradingName: 'EABL',
    dateOfIncorporation: '1922-05-15',
    registeredAddress: 'Ruiri Road, Industrial Area, Nairobi',
    county: 'Nairobi',
    industrySectors: ['Food and Beverage Manufacturing', 'Agro-processing'],
    numberOfSites: '4',
    geographicSpread: 'Nairobi, Kisumu, Mombasa, Thika',
    certifications: ['ISO 9001', 'ISO 14001', 'ISO 45001'],
    facilityAge: '100',
    facilitySize: '180000',
    operatingHours: '24/7',
    operatingDays: '350',
    monitoringSystems: ['SCADA', 'ERP', 'MES'],
    connectivity: 'Reliable fibre broadband'
  },
  {
    id: 'company_kapa_oil',
    name: 'Kapa Oil Refineries',
    registrationNumber: 'KOR/1960/005',
    industry: 'manufacturing',
    location: 'Mombasa, Kenya',
    contactName: 'James Otieno',
    contactTitle: 'Factory Manager',
    email: 'demo@kapaoil.com',
    phone: '+254 722 567 890',
    website: 'https://kapaoil.com',
    memberSince: '2025-11-15',
    companySize: '201-500',
    yearEstablished: '1960',
    description: 'Leading manufacturer of edible oils and fats.',
    tradingName: 'Kapa Oil',
    dateOfIncorporation: '1960-02-01',
    registeredAddress: 'Likoni Road, Industrial Area, Mombasa',
    county: 'Mombasa',
    industrySectors: ['Manufacturing', 'Agro-processing'],
    numberOfSites: '2',
    geographicSpread: 'Mombasa, Nairobi',
    certifications: ['ISO 9001', 'ISO 14001'],
    facilityAge: '60',
    facilitySize: '100000',
    operatingHours: '24/7',
    operatingDays: '340',
    monitoringSystems: ['SCADA', 'ERP'],
    connectivity: 'Reliable fibre broadband'
  }
];

// Initial demo submissions - UPDATED companyId references
const INITIAL_DEMO_SUBMISSIONS: DemoSubmission[] = [
  // Kenya Breweries Ltd
  {
    id: 'sub-001',
    companyId: 'company_kenya_breweries',
    companyName: 'Kenya Breweries Limited',
    companyRegistration: 'KBL/1990/4567',
    problemTitle: 'Bottling Line Conveyor Misalignment',
    description: 'Conveyor belt misaligns 3-4 times per week, causing 2-3 hours of downtime each time. The misalignment occurs due to gradual tension drift in the belt system, which is only detected after product jams occur.',
    plainDescription: 'Conveyor belt misaligns 3-4 times per week, causing 2-3 hours of downtime each time.',
    estimatedImpact: 'KES 14.4M per year',
    estimatedImpactValue: 14400000,
    urgency: 'high',
    status: 'accepted',
    submittedAt: '2026-02-10T09:30:00Z',
    reviewedAt: '2026-02-15T14:20:00Z',
    reviewNotes: 'Excellent opportunity. Converting to problem bank entry.',
    documents: [
      { name: 'Downtime_Logs_2025.xlsx', url: '#', size: '2.4 MB', type: 'Excel' },
      { name: 'Maintenance_Records.pdf', url: '#', size: '1.8 MB', type: 'PDF' }
    ],
    category: ['downtime', 'maintenance'],
    contactName: 'John Mwangi',
    contactTitle: 'Plant Manager',
    contactEmail: 'demo@kenyabreweries.com',
    contactPhone: '+254 722 123 456',
    problemCategory: 'downtime',
    totalAnnualCost: 14400000,
    consentStorage: true,
    consentContact: true,
    consentAnonymised: true,
    consentUpdates: true,
    consentSquad: true,
    dataSharingLevel: 'identified-after-nda',
    declarationAgreed: true
  },
  {
    id: 'sub-002',
    companyId: 'company_kenya_breweries',
    companyName: 'Kenya Breweries Limited',
    companyRegistration: 'KBL/1990/4567',
    problemTitle: 'Packaging Machine Frequent Jams',
    description: 'Packaging line stops 5-6 times daily due to misaligned sealing mechanism.',
    plainDescription: 'Packaging line stops 5-6 times daily due to misaligned sealing mechanism.',
    estimatedImpact: 'KES 8.2M per year',
    estimatedImpactValue: 8200000,
    urgency: 'medium',
    status: 'under_review',
    submittedAt: '2026-03-01T14:20:00Z',
    documents: [{ name: 'Packaging_Logs_March.xlsx', url: '#', size: '1.2 MB', type: 'Excel' }],
    category: ['downtime', 'quality_control'],
    contactName: 'John Mwangi',
    contactTitle: 'Plant Manager',
    contactEmail: 'demo@kenyabreweries.com',
    totalAnnualCost: 8200000,
    consentStorage: true,
    consentContact: true,
    consentAnonymised: true,
    consentUpdates: true,
    consentSquad: true,
    dataSharingLevel: 'anonymised-only',
    declarationAgreed: true
  },
  // Bidco Africa
  {
    id: 'sub-003',
    companyId: 'company_bidco',
    companyName: 'Bidco Africa',
    companyRegistration: 'BID/1985/002',
    problemTitle: 'Oil Filling Line Inefficiency',
    description: 'Oil filling machines have inconsistent fill levels, causing 8% product giveaway.',
    plainDescription: 'Oil filling machines have inconsistent fill levels, causing 8% product giveaway.',
    estimatedImpact: 'KES 22.5M per year',
    estimatedImpactValue: 22500000,
    urgency: 'high',
    status: 'accepted',
    submittedAt: '2026-02-05T10:00:00Z',
    reviewedAt: '2026-02-10T09:30:00Z',
    reviewNotes: 'High impact opportunity.',
    documents: [{ name: 'Filling_Data.xlsx', url: '#', size: '3.2 MB', type: 'Excel' }],
    category: ['process_inefficiency', 'quality_control'],
    contactName: 'Sarah Wanjiku',
    contactTitle: 'Operations Director',
    contactEmail: 'demo@bidcoafrica.com',
    totalAnnualCost: 22500000,
    consentStorage: true,
    consentContact: true,
    consentAnonymised: true,
    consentUpdates: true,
    consentSquad: true,
    dataSharingLevel: 'identified-after-nda',
    declarationAgreed: true
  }
];

// Storage keys
const STORAGE_KEYS = {
  COMPANY_TOKEN: 'demo_company_token',
  COMPANY_ID: 'demo_company_id',
  COMPANY_DATA: 'demo_company_data',
  SUBMISSIONS: 'demo_company_submissions',
  DEMO_MODE_ACTIVE: 'demo_company_mode_active',
  SUBMISSIONS_GENERATED: 'demo_submissions_generated'
};

export const demoCompanyService = {
  initializeDemoData: (): { companies: DemoCompany[]; submissions: DemoSubmission[] } => {
    const existingSubmissions = localStorage.getItem(STORAGE_KEYS.SUBMISSIONS);
    
    if (existingSubmissions) {
      try {
        const submissions = JSON.parse(existingSubmissions);
        return { companies: DEMO_COMPANIES, submissions };
      } catch {
        // Invalid JSON, use initial
      }
    }

    localStorage.setItem(STORAGE_KEYS.SUBMISSIONS, JSON.stringify(INITIAL_DEMO_SUBMISSIONS));
    localStorage.setItem(STORAGE_KEYS.SUBMISSIONS_GENERATED, 'true');
    
    return { companies: DEMO_COMPANIES, submissions: INITIAL_DEMO_SUBMISSIONS };
  },

  isDemoModeActive: (): boolean => {
    return localStorage.getItem(STORAGE_KEYS.DEMO_MODE_ACTIVE) === 'true';
  },

  loadCompanyData: (companyId: string): { company: DemoCompany | null; submissions: DemoSubmission[] } => {
    const company = DEMO_COMPANIES.find(c => c.id === companyId) || null;
    
    const submissionsData = localStorage.getItem(STORAGE_KEYS.SUBMISSIONS);
    let allSubmissions: DemoSubmission[] = [];
    
    if (submissionsData) {
      try {
        allSubmissions = JSON.parse(submissionsData);
      } catch {
        allSubmissions = INITIAL_DEMO_SUBMISSIONS;
      }
    } else {
      allSubmissions = INITIAL_DEMO_SUBMISSIONS;
      localStorage.setItem(STORAGE_KEYS.SUBMISSIONS, JSON.stringify(allSubmissions));
    }
    
    const companySubmissions = allSubmissions.filter(s => s.companyId === companyId);
    
    if (company) {
      localStorage.setItem(STORAGE_KEYS.COMPANY_DATA, JSON.stringify(company));
    }
    
    return { company, submissions: companySubmissions };
  },

  loginAsDemo: (companyId: string): { success: boolean; company?: DemoCompany; error?: string } => {
    const company = DEMO_COMPANIES.find(c => c.id === companyId);
    
    if (!company) {
      console.error('Company not found:', companyId);
      return { success: false, error: 'Company not found' };
    }

    try {
      // Clear any existing data
      localStorage.removeItem(STORAGE_KEYS.COMPANY_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.COMPANY_ID);
      localStorage.removeItem(STORAGE_KEYS.DEMO_MODE_ACTIVE);
      localStorage.removeItem(STORAGE_KEYS.COMPANY_DATA);
      localStorage.removeItem(STORAGE_KEYS.SUBMISSIONS);

      // Set demo mode active
      localStorage.setItem(STORAGE_KEYS.DEMO_MODE_ACTIVE, 'true');
      localStorage.setItem(STORAGE_KEYS.COMPANY_TOKEN, 'demo-token-' + companyId);
      localStorage.setItem(STORAGE_KEYS.COMPANY_ID, companyId);
      
      // Store company data
      localStorage.setItem(STORAGE_KEYS.COMPANY_DATA, JSON.stringify(company));
      
      // Get only this company's submissions
      const companySubmissions = INITIAL_DEMO_SUBMISSIONS.filter(s => s.companyId === companyId);
      localStorage.setItem(STORAGE_KEYS.SUBMISSIONS, JSON.stringify(companySubmissions));
      
      console.log('Demo login successful:', { company: company.name, submissions: companySubmissions.length });
      
      return { success: true, company };
    } catch (error) {
      console.error('Demo login error:', error);
      return { success: false, error: 'Failed to store company data' };
    }
  },

  logout: () => {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  },

  getCurrentCompany: (): DemoCompany | null => {
    const companyData = localStorage.getItem(STORAGE_KEYS.COMPANY_DATA);
    if (!companyData) return null;
    
    try {
      return JSON.parse(companyData);
    } catch {
      return null;
    }
  },

  getSubmissions: (): DemoSubmission[] => {
    const submissionsData = localStorage.getItem(STORAGE_KEYS.SUBMISSIONS);
    const companyId = localStorage.getItem(STORAGE_KEYS.COMPANY_ID);
    
    if (!submissionsData || !companyId) return [];
    
    try {
      const allSubmissions = JSON.parse(submissionsData);
      return allSubmissions.filter((s: DemoSubmission) => s.companyId === companyId);
    } catch {
      return [];
    }
  },

  getAllCompanies: (): DemoCompany[] => {
    return DEMO_COMPANIES;
  },

  getCompaniesByIndustry: (industry: string): DemoCompany[] => {
    return DEMO_COMPANIES.filter(c => c.industry === industry);
  },

  getSubmissionById: (submissionId: string): DemoSubmission | null => {
    const submissions = demoCompanyService.getSubmissions();
    return submissions.find(s => s.id === submissionId) || null;
  },

  getAllSubmissions: (): DemoSubmission[] => {
    const submissionsData = localStorage.getItem(STORAGE_KEYS.SUBMISSIONS);
    if (!submissionsData) return [];
    
    try {
      return JSON.parse(submissionsData);
    } catch {
      return [];
    }
  },

  createSubmission: (companyId: string, submissionData: any): DemoSubmission => {
    const company = DEMO_COMPANIES.find(c => c.id === companyId);
    if (!company) throw new Error('Company not found');
    
    const newSubmission: DemoSubmission = {
      id: `sub-${Date.now()}`,
      companyId,
      companyName: company.name,
      companyRegistration: company.registrationNumber,
      status: 'pending',
      submittedAt: new Date().toISOString(),
      documents: [],
      category: ['other'],
      ...submissionData
    };
    
    const submissionsData = localStorage.getItem(STORAGE_KEYS.SUBMISSIONS);
    let allSubmissions: DemoSubmission[] = [];
    
    if (submissionsData) {
      try {
        allSubmissions = JSON.parse(submissionsData);
      } catch {
        allSubmissions = INITIAL_DEMO_SUBMISSIONS;
      }
    } else {
      allSubmissions = INITIAL_DEMO_SUBMISSIONS;
    }
    
    allSubmissions.push(newSubmission);
    localStorage.setItem(STORAGE_KEYS.SUBMISSIONS, JSON.stringify(allSubmissions));
    
    return newSubmission;
  },

  getStats: (companyId: string) => {
    const submissions = demoCompanyService.getSubmissions();
    
    return {
      total: submissions.length,
      pending: submissions.filter(s => s.status === 'pending').length,
      underReview: submissions.filter(s => s.status === 'under_review').length,
      accepted: submissions.filter(s => s.status === 'accepted').length,
      rejected: submissions.filter(s => s.status === 'rejected').length,
      totalImpact: submissions.reduce((sum, s) => sum + (s.estimatedImpactValue || 0), 0),
      acceptanceRate: submissions.length > 0 
        ? Math.round((submissions.filter(s => s.status === 'accepted').length / submissions.length) * 100)
        : 0
    };
  },

  seedAllData: () => {
    demoCompanyService.logout();
    localStorage.setItem(STORAGE_KEYS.DEMO_MODE_ACTIVE, 'true');
    
    return {
      companies: DEMO_COMPANIES,
      submissions: INITIAL_DEMO_SUBMISSIONS,
      stats: {
        totalCompanies: DEMO_COMPANIES.length,
        totalSubmissions: INITIAL_DEMO_SUBMISSIONS.length,
        totalImpact: INITIAL_DEMO_SUBMISSIONS.reduce((sum, s) => sum + (s.estimatedImpactValue || 0), 0)
      }
    };
  },

  clearAllData: () => {
    demoCompanyService.logout();
  }
};